import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import {
  opsApi, cancellationApi, holdApi, vendorResponseApi, trackingApi,
} from '../../api/services';

// Static map — Tailwind needs full class strings to include them in the build.
const COLOR_CLASSES = {
  blue:   'bg-blue-600 hover:bg-blue-700',
  green:  'bg-green-600 hover:bg-green-700',
  red:    'bg-red-600 hover:bg-red-700',
  amber:  'bg-amber-600 hover:bg-amber-700',
  orange: 'bg-orange-600 hover:bg-orange-700',
  yellow: 'bg-yellow-600 hover:bg-yellow-700',
};
function btnColor(c) { return COLOR_CLASSES[c] || COLOR_CLASSES.blue; }

// Generic note-only modal used by several actions.
function NoteModal({ isOpen, onClose, onDone, title, label, submitLabel, submitColor, onSubmit }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!note.trim()) { toast.error('Please enter a note'); return; }
    setSaving(true);
    try {
      await onSubmit(note.trim());
      toast.success('Done');
      setNote('');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm disabled:opacity-50 ${btnColor(submitColor)}`}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// Simple confirm modal with no text input.
function ConfirmModal({ isOpen, onClose, onDone, title, message, submitLabel, submitColor, onSubmit }) {
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await onSubmit();
      toast.success('Done');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-3">
        <p className="text-sm text-gray-700">{message}</p>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className={`flex items-center gap-2 px-4 py-2 text-white rounded-lg text-sm disabled:opacity-50 ${btnColor(submitColor)}`}>
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} {submitLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Ops-specific modals ---------------------------------------------------

export function EscalateToCCModal({ isOpen, onClose, onDone, order }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Escalate to CC"
      label="Note (what did email try? why escalating?) *"
      submitLabel="Escalate"
      submitColor="orange"
      onSubmit={(note) => opsApi.escalateToCC(order.id, { note })}
    />
  );
}

export function CustomerRespondedModal({ isOpen, onClose, onDone, order }) {
  const [note, setNote] = useState('');
  const [toTeam, setToTeam] = useState('dispatch_admin');
  const [saving, setSaving] = useState(false);
  const [updateAddress, setUpdateAddress] = useState(false);
  const [addr, setAddr] = useState({
    customer_name: '', address1: '', address2: '', landmark: '',
    city: '', zone: '', postcode: '', country: '', customer_phone: '',
  });

  // Pre-fill the address form from the order's current shipping fields
  // whenever the modal opens or the order changes.
  useEffect(() => {
    if (!isOpen || !order) return;
    setAddr({
      customer_name:  order.customer_name     || '',
      address1:       order.shipping_address1 || '',
      address2:       order.shipping_address2 || '',
      landmark:       order.landmark          || '',
      city:           order.shipping_city     || '',
      zone:           order.shipping_zone     || '',
      postcode:       order.shipping_postcode || '',
      country:        order.shipping_country  || '',
      customer_phone: order.customer_phone    || '',
    });
    setUpdateAddress(false);
    setNote('');
  }, [isOpen, order]);

  async function save() {
    if (!note.trim()) { toast.error('Please enter customer\'s response'); return; }
    if (updateAddress) {
      for (const k of ['address1', 'city', 'postcode', 'country']) {
        if (!String(addr[k] || '').trim()) {
          toast.error(`${k.replace('_', ' ')} is required for address update`);
          return;
        }
      }
    }
    setSaving(true);
    try {
      const body = { note: note.trim(), toTeam };
      if (updateAddress) body.correctedAddress = addr;
      await opsApi.customerResponded(order.id, body);
      toast.success(updateAddress
        ? 'Address correction proposed — pending admin review'
        : 'Customer response recorded');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  function field(key, label, opts = {}) {
    return (
      <label className="block">
        <span className="block text-xs font-medium text-gray-700 mb-1">{label}{opts.required && ' *'}</span>
        <input type="text" value={addr[key]} onChange={(e) => setAddr((a) => ({ ...a, [key]: e.target.value }))}
               className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
      </label>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Customer responded">
      <div className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Response summary *</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Route to</span>
          <select value={toTeam} onChange={(e) => setToTeam(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  disabled={updateAddress}>
            <option value="dispatch_admin">Dispatch admin</option>
            <option value="dispatch_user">Dispatch user</option>
          </select>
          {updateAddress && (
            <span className="block text-xs text-gray-500 mt-1">Address correction always routes to dispatch_admin for review.</span>
          )}
        </label>

        <label className="flex items-center gap-2 pt-1">
          <input type="checkbox" checked={updateAddress} onChange={(e) => setUpdateAddress(e.target.checked)} />
          <span className="text-sm font-medium text-gray-700">Also propose a shipping address update</span>
        </label>

        {updateAddress && (
          <div className="border border-blue-200 bg-blue-50 rounded-lg p-3 space-y-2">
            <div className="text-xs text-blue-900">
              The corrected address is held as a <strong>proposal</strong> until admin approves. The order's current address is unchanged until then.
            </div>
            {field('customer_name', 'Customer name')}
            <div className="grid grid-cols-2 gap-2">
              {field('address1', 'Address line 1', { required: true })}
              {field('address2', 'Address line 2')}
            </div>
            {field('landmark', 'Landmark')}
            <div className="grid grid-cols-2 gap-2">
              {field('city', 'City', { required: true })}
              {field('zone', 'State / Zone')}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {field('postcode', 'Postcode', { required: true })}
              {field('country', 'Country', { required: true })}
            </div>
            {field('customer_phone', 'Customer phone')}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {updateAddress ? 'Record & propose address' : 'Record'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Tracking modals -------------------------------------------------------

// Manually mark a stuck shipment Delivered when the carrier site / customer
// confirms delivery off-system (issue #11). Remarks are mandatory.
export function MarkDeliveredModal({ isOpen, onClose, onDone, shipment }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Mark as delivered"
      label="Remarks — how was delivery confirmed? (e.g. India Post site shows delivered on 12 May, or customer confirmed receipt) *"
      submitLabel="Mark delivered"
      submitColor="green"
      onSubmit={(remarks) => trackingApi.markDelivered(shipment.id, { remarks })}
    />
  );
}

// Record an extra parcel on an already-dispatched order (#33). The vendor
// splits at dispatch and sends two tracking numbers, usually without saying
// which items are in which parcel — so we ask only for the tracking number.
export function AddParcelModal({ isOpen, onClose, onDone, order }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Add another parcel"
      label="Tracking number for the extra parcel *"
      submitLabel="Add parcel"
      submitColor="blue"
      onSubmit={(trackingNumber) => trackingApi.addParcel(order.id, { trackingNumber })}
    />
  );
}

// ---- Cancellation modals ---------------------------------------------------

export function RequestCancellationModal({ isOpen, onClose, onDone, order, byDispatchUser = false }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title={byDispatchUser ? 'Cancel — vendor cannot fulfil' : 'Request cancellation'}
      label="Reason *"
      submitLabel="Request"
      submitColor="amber"
      onSubmit={(reasonNote) =>
        byDispatchUser
          ? cancellationApi.requestByUserVendorCannotFulfil(order.id, { reasonNote })
          : cancellationApi.requestByOps(order.id, { reasonNote })
      }
    />
  );
}

export function ConfirmCancellationModal({ isOpen, onClose, onDone, order }) {
  return (
    <ConfirmModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Confirm cancellation"
      message="Vendor has stopped the shipment. This will hand the order back to Ops for refund processing."
      submitLabel="Confirm cancellation"
      submitColor="green"
      onSubmit={() => cancellationApi.confirm(order.id)}
    />
  );
}

export function FailCancellationModal({ isOpen, onClose, onDone, order }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Cancellation failed — vendor already shipped"
      label="Notes (tracking, vendor response, etc.)"
      submitLabel="Mark failed"
      submitColor="red"
      onSubmit={(reasonNote) => cancellationApi.fail(order.id, { reasonNote })}
    />
  );
}

export function CloseCancellationModal({ isOpen, onClose, onDone, order }) {
  return (
    <ConfirmModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Close cancellation"
      message="Confirm the refund has been processed. This will mark the cancellation as closed (terminal)."
      submitLabel="Close"
      submitColor="green"
      onSubmit={() => cancellationApi.close(order.id)}
    />
  );
}

export function CustomerPaidAfterDeliveryModal({ isOpen, onClose, onDone, order }) {
  return (
    <ConfirmModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Customer paid after delivery"
      message="Customer paid via the link. This closes the order as completed."
      submitLabel="Mark paid"
      submitColor="green"
      onSubmit={() => cancellationApi.customerPaidAfterDelivery(order.id)}
    />
  );
}

export function MarkTroublesomeModal({ isOpen, onClose, onDone, order }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Mark customer troublesome"
      label="Notes — customer refused / stopped responding"
      submitLabel="Mark troublesome"
      submitColor="red"
      onSubmit={(reasonNote) => cancellationApi.markCustomerTroublesome(order.id, { reasonNote })}
    />
  );
}

const REFUND_STATUSES = [
  { value: 'none',               label: 'None' },
  { value: 'refund_pending',     label: 'Refund pending' },
  { value: 'refund_processed',   label: 'Refund processed' },
  { value: 'payment_link_sent',  label: 'Payment link sent' },
  { value: 'payment_collected',  label: 'Payment collected' },
  { value: 'payment_declined',   label: 'Payment declined' },
  { value: 'no_response',        label: 'No response' },
];

export function UpdateRefundStatusModal({ isOpen, onClose, onDone, order }) {
  const [status, setStatus] = useState(order?.refund_payment_status || 'none');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await cancellationApi.updateRefundPaymentStatus(order.id, { status, note: note || null });
      toast.success('Refund status updated');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update refund / payment status">
      <div className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Status</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            {REFUND_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</span>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Update
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---- Hold modals -----------------------------------------------------------

export function RequestHoldModal({ isOpen, onClose, onDone, order }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Request hold"
      label="Why is customer holding? *"
      submitLabel="Request hold"
      submitColor="yellow"
      onSubmit={(reasonNote) => holdApi.request(order.id, { reasonNote })}
    />
  );
}

export function ReleaseHoldModal({ isOpen, onClose, onDone, order, onRequestLink }) {
  // DMS-20260514: a reship that's never been linked can't move to in_review
  // (the orders_reship_must_link_or_be_unlinked CHECK rejects it). Show a
  // link-first prompt instead of letting the release fail.
  const needsLink = !!order?.is_reship
    && !order?.original_order_id
    && !order?.original_order_id_external;

  if (needsLink) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Link original order first">
        <div className="space-y-3">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            This is a <strong>reship</strong> that hasn't been linked to its
            Original Order yet. A reship must be linked before it can be
            released back into the normal flow.
          </div>
          <p className="text-sm text-gray-700">
            Link the Original Order ID, then return here to release the hold.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >Cancel</button>
            <button
              onClick={() => { onClose(); onRequestLink?.(); }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
            >Link original order</button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <ConfirmModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Release hold"
      message="Customer is ready to proceed. This returns the order to the normal flow."
      submitLabel="Release"
      submitColor="green"
      onSubmit={() => holdApi.release(order.id)}
    />
  );
}

export function MarkReminderSentModal({ isOpen, onClose, onDone, order }) {
  return (
    <ConfirmModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Mark reminder sent"
      message="Confirm the day-15 hold reminder email has been sent manually."
      submitLabel="Mark sent"
      submitColor="blue"
      onSubmit={() => holdApi.markReminderSent(order.id)}
    />
  );
}

// ---- Vendor response modals -----------------------------------------------

const RESPONSE_TYPES = [
  { value: 'out_of_stock',                    label: 'Product out of stock' },
  { value: 'delayed',                         label: 'Delayed' },
  { value: 'alternative_available',           label: 'Alternative available' },
  { value: 'strength_change',                 label: 'Strength change' },
  { value: 'wrong_product_shipped',           label: 'Wrong product shipped' },
  // Vendor-update reasons captured especially after TN is recorded.
  // (Kalpana 2026-05-22.) All route through the customer-consultation flow.
  { value: 'product_brand_changed',           label: 'Product Brand changed' },
  { value: 'product_not_supported',           label: 'Product Not Supported' },
  { value: 'export_restricted',               label: 'Export Restricted' },
  { value: 'short_expiry',                    label: 'Short Expiry' },
  { value: 'country_service_unavailable',     label: 'Country Service Unavailable' },
  { value: 'india_sale_only',                 label: 'India Sale Only' },
  { value: 'sildenafil_above_100mg_blocked',  label: 'Sildenafil >100mg Not Allowed via India Post' },
];

export function RecordVendorResponseModal({ isOpen, onClose, onDone, order, items = [] }) {
  const [responseType, setResponseType] = useState('out_of_stock');
  const [description, setDescription] = useState('');
  const [altName, setAltName] = useState('');
  const [altBrand, setAltBrand] = useState('');
  const [altManufacturer, setAltManufacturer] = useState('');
  const [altStrength, setAltStrength] = useState('');
  const [altQuantity, setAltQuantity] = useState('');
  const [saving, setSaving] = useState(false);

  // Response types where the vendor typically offers a replacement product.
  // For these, surface the alt-input fields so Dispatch can capture the
  // replacement and the OPS approval auto-substitutes the order_item.
  // Round 6 (Kalpana 2026-05-26): added product_brand_changed + out_of_stock
  // so the "auto-substitute on approval" flow works regardless of which
  // exception type Dispatch picks.
  const altCapable = ['alternative_available', 'strength_change',
                      'product_brand_changed', 'out_of_stock'];
  const isAlt = altCapable.includes(responseType);
  const altRequired = responseType === 'alternative_available' || responseType === 'strength_change';

  // Active items on the order — alt vendor responses target a specific item.
  // Round 7 (Kalpana 2026-05-29, IDM260527126C): multi-item orders need an
  // item picker so customerApprovedAlt knows which order_item to substitute.
  // Without this, the auto-substitution silently skipped for multi-item orders.
  const currentItems = (items || []).filter((it) => !it.cancelled_at);
  const needsItemPicker = isAlt && currentItems.length > 1;
  const [targetItemId, setTargetItemId] = useState(currentItems[0]?.id || null);
  // Keep the picker selection in sync if the items list changes (modal stays
  // mounted on conditionally-rendered open, but defensive sync helps).
  useEffect(() => {
    if (!currentItems.find((it) => it.id === targetItemId)) {
      setTargetItemId(currentItems[0]?.id || null);
    }
  }, [items, responseType]);
  const targetItem = currentItems.find((it) => it.id === targetItemId) || currentItems[0] || null;

  async function save() {
    if (!description.trim()) { toast.error('Description required'); return; }
    if (altRequired && !altName.trim()) { toast.error('Alternative product name required'); return; }
    if (isAlt && currentItems.length > 0 && !targetItem) {
      toast.error('Pick which item this alternative is for'); return;
    }
    // v2 tracks vendors per-shipment, so orders.vendor_id is null. Use the
    // order's current_owner_vendor_id (set by the handoff when dispatched)
    // — that's the vendor the order is currently with and whose voice this
    // response represents.
    const vendorId = order.current_owner_vendor_id || order.vendor_id;
    if (!vendorId) {
      toast.error("Can't record — this order isn't currently with a vendor");
      return;
    }
    setSaving(true);
    try {
      await vendorResponseApi.record(order.id, {
        responseType,
        description: description.trim(),
        onBehalfOfVendorId: vendorId,
        // Round 7: send orderItemId so customerApprovedAlt can auto-substitute
        // the correct item. For non-alt response types (delayed / wrong_product
        // / other), leave it null — the response is about the whole order.
        orderItemId: isAlt ? (targetItem?.id || null) : null,
        alt: isAlt ? {
          product_name: altName,
          brand_name: altBrand || null,
          manufacturer: altManufacturer || null,
          strength: altStrength || null,
          quantity: altQuantity ? parseInt(altQuantity, 10) : null,
        } : null,
      });
      toast.success('Vendor response recorded');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Record vendor response">
      <div className="space-y-3">
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Response type *</span>
          <select value={responseType} onChange={(e) => setResponseType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            {RESPONSE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">What did the vendor say? *</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        {isAlt && (
          <div className="space-y-2 pt-2 border-t border-gray-100">
            <div className="text-xs font-semibold text-gray-600">
              Alternative offered{altRequired ? ' *' : ' (optional)'}
            </div>
            {/* Item picker / reference. Multi-item orders REQUIRE the
                picker so the auto-substitution knows which item to apply
                the alt to (Kalpana 2026-05-29, IDM260527126C). Single-item
                orders just show the current product as reference. */}
            {needsItemPicker ? (
              <label className="block">
                <span className="block text-xs font-medium text-gray-700 mb-1">Which item is this alternative for? *</span>
                <select value={targetItemId || ''} onChange={(e) => setTargetItemId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  {currentItems.map((it) => (
                    <option key={it.id} value={it.id}>
                      {it.product_name}{it.indian_brand ? ` (${it.indian_brand})` : ''} · qty {it.quantity}
                    </option>
                  ))}
                </select>
                <span className="block text-[10px] text-gray-400 mt-0.5">
                  Enter the <b>alternative</b> below — what the vendor is offering instead.
                </span>
              </label>
            ) : currentItems.length === 1 ? (
              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5">
                Current on order:{' '}
                <span className="text-gray-700">{currentItems[0].product_name}</span>
                {currentItems[0].indian_brand ? ` (${currentItems[0].indian_brand})` : ''}
                {' · qty '}{currentItems[0].quantity}
                <div className="text-[10px] text-gray-400 mt-0.5">
                  Enter the <b>alternative</b> below — what the vendor is offering instead.
                </div>
              </div>
            ) : null}
            <input value={altName} onChange={(e) => setAltName(e.target.value)}
                   placeholder={altRequired ? 'Alternative product name *' : 'Alternative product name'}
                   className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <input value={altBrand} onChange={(e) => setAltBrand(e.target.value)} placeholder="Alt brand"
                     className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input value={altManufacturer} onChange={(e) => setAltManufacturer(e.target.value)} placeholder="Alt manufacturer"
                     className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={altStrength} onChange={(e) => setAltStrength(e.target.value)} placeholder="Alt strength"
                     className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <input value={altQuantity} onChange={(e) => setAltQuantity(e.target.value)} placeholder="Alt quantity" type="number"
                     className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Record
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function CustomerApprovedAltModal({ isOpen, onClose, onDone, order, vendorResponseId }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Customer approved alternative"
      label="Note (optional)"
      submitLabel="Approve"
      submitColor="green"
      onSubmit={(reasonNote) =>
        vendorResponseApi.customerApprovedAlt(order.id, { vendorResponseId, reasonNote })
      }
    />
  );
}

export function CustomerRejectedAltModal({ isOpen, onClose, onDone, order, vendorResponseId }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Customer rejected alternative"
      label="Note (optional)"
      submitLabel="Reject"
      submitColor="red"
      onSubmit={(reasonNote) =>
        vendorResponseApi.customerRejectedAlt(order.id, { vendorResponseId, reasonNote })
      }
    />
  );
}

export function ShipPartialHoldOosModal({ isOpen, onClose, onDone, order }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Ship available items, hold OOS"
      label="Note"
      submitLabel="Confirm"
      submitColor="blue"
      onSubmit={(reasonNote) => vendorResponseApi.shipPartialHoldOos(order.id, { reasonNote })}
    />
  );
}

export function ShipPartialCancelOosModal({ isOpen, onClose, onDone, order }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Ship available items, cancel OOS"
      label="Note"
      submitLabel="Confirm"
      submitColor="blue"
      onSubmit={(reasonNote) => vendorResponseApi.shipPartialCancelOos(order.id, { reasonNote })}
    />
  );
}

export function PresentMultiAltModal({ isOpen, onClose, onDone, order }) {
  return (
    <NoteModal
      isOpen={isOpen} onClose={onClose} onDone={onDone}
      title="Present multiple alternatives to customer"
      label="Note (list the options)"
      submitLabel="Send to Ops"
      submitColor="blue"
      onSubmit={(reasonNote) => vendorResponseApi.presentMultiAlt(order.id, { reasonNote })}
    />
  );
}

export function ReshipReassignVendorModal({ isOpen, onClose, onDone, order, vendors = [] }) {
  const [newVendorId, setNewVendorId] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!newVendorId) { toast.error('Pick a vendor'); return; }
    setSaving(true);
    try {
      await vendorResponseApi.reshipReassignVendor(order.id, { newVendorId, reasonNote: reasonNote || null });
      toast.success('Reship reassigned');
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setSaving(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Reship — reassign vendor">
      <div className="space-y-3">
        <p className="text-xs text-gray-500">Reship-only direct reassign (§7.9) — no Ops involvement needed.</p>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">New vendor *</span>
          <select value={newVendorId} onChange={(e) => setNewVendorId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="">— select vendor —</option>
            {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-gray-700 mb-1">Note (optional)</span>
          <textarea value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
          <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Reassign
          </button>
        </div>
      </div>
    </Modal>
  );
}
